import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Typography, Box, Container, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    _T: (key: string) => string;
}

interface ErrorState {
    hasError: boolean;
    error: Error | null;
}

// ErrorBoundary must be a class component as React hooks don't support error boundaries
class ErrorBoundaryClass extends Component<Props, ErrorState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null
        };
    }

    static getDerivedStateFromError(error: Error): ErrorState {
        return {
            hasError: true,
            error
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('ChunkErrorBoundary caught an error:', error, errorInfo);

        // Check if it's a chunk load error
        if (
            error.name === 'ChunkLoadError' ||
            error.message.includes('Loading chunk') ||
            error.message.includes('Failed to fetch dynamically imported module')
        ) {
            // Clear cache and reload after a short delay
            setTimeout(() => {
                if ('caches' in (window as any)) {
                    caches.keys().then(cacheNames => {
                        cacheNames.forEach(cacheName => {
                            caches.delete(cacheName);
                        });
                        (window as any).location.reload();
                    });
                } else {
                    (window as any).location.reload();
                }
            }, 1500);
        }
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback || <ErrorFallbackUI onRefresh={() => (window as any).location.reload()} _T={this.props._T}/>;
        }
        return this.props.children;
    }
}

// Functional component for the error UI
const ErrorFallbackUI = ({ onRefresh, _T }: { onRefresh: () => void; _T: (key: string) => string }) => {
    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" component="h2" gutterBottom>
                    {_T("Something went wrong loading this page")}
                </Typography>
                <Typography color="text.secondary">
                {_T("This is usually caused by a network issue or a recent update.")}
                </Typography>
                <Box mt={3}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onRefresh}
                        startIcon={<RefreshIcon />}
                    >
                        {_T("Reload Page")}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

// Main functional component that uses the class-based error boundary
const ChunkErrorBoundary = (props: Props) => {
    return <ErrorBoundaryClass {...props} />;
};

export default ChunkErrorBoundary;
